/**
 * Platform-specific category/channel options and extra config schemas.
 * Mirrors backend MRSSManagementApp/platform_constants.py — keep in sync.
 */

export const PLATFORM_CATEGORIES = {
  DAILYMOTION: [
    { value: "animals",    label: "Animals" },
    { value: "auto",       label: "Auto" },
    { value: "creation",   label: "Creation" },
    { value: "fun",        label: "Fun" },
    { value: "lifestyle",  label: "Lifestyle" },
    { value: "music",      label: "Music" },
    { value: "news",       label: "News" },
    { value: "people",     label: "People" },
    { value: "school",     label: "School" },
    { value: "shortfilms", label: "Short Films" },
    { value: "sport",      label: "Sport" },
    { value: "tech",       label: "Tech" },
    { value: "travel",     label: "Travel" },
    { value: "tv",         label: "TV" },
    { value: "videogames", label: "Video Games" },
    { value: "webcam",     label: "Webcam" },
  ],
  YOUTUBE: [
    { value: "2",  label: "Autos & Vehicles" },
    { value: "10", label: "Music" },
    { value: "15", label: "Pets & Animals" },
    { value: "17", label: "Sports" },
    { value: "19", label: "Travel & Events" },
    { value: "20", label: "Gaming" },
    { value: "22", label: "People & Blogs" },
    { value: "23", label: "Comedy" },
    { value: "24", label: "Entertainment" },
    { value: "25", label: "News & Politics" },
    { value: "26", label: "How-to & Style" },
    { value: "27", label: "Education" },
    { value: "28", label: "Science & Technology" },
    { value: "29", label: "Nonprofits & Activism" },
  ],
  MSN: [
    { value: "news",          label: "News" },
    { value: "entertainment", label: "Entertainment" },
    { value: "sports",        label: "Sports" },
    { value: "lifestyle",     label: "Lifestyle" },
    { value: "travel",        label: "Travel" },
    { value: "technology",    label: "Technology" },
    { value: "health",        label: "Health" },
    { value: "finance",       label: "Finance" },
    { value: "food",          label: "Food" },
    { value: "autos",         label: "Autos" },
  ],
  // FACEBOOK intentionally absent — uses media_type (extra_config) instead of a category
};

/**
 * Extra boolean/select/number fields per platform shown below the category dropdown.
 * Schema: { fieldName: { label, type ("bool" | "select" | "number"), options?, default, min? } }
 */
export const PLATFORM_EXTRA_CONFIG_SCHEMA = {
  TIKTOK: {
    // TikTok uses a frame of the video itself as the cover instead of an
    // uploaded thumbnail. Value = time offset (seconds) into the final video.
    cover_timestamp: {
      label: "Cover Frame Time (seconds)",
      type: "number",
      default: null,
      min: 0,
    },
  },
  DAILYMOTION: {
    private: {
      label: "Private",
      type: "bool",
      default: false,
    },
    advertising_blocked: {
      label: "Block Instream Ads",
      type: "bool",
      default: false,
    },
  },
  YOUTUBE: {
    privacy_status: {
      label: "Privacy Status",
      type: "select",
      options: [
        { value: "public",   label: "Public" },
        { value: "unlisted", label: "Unlisted" },
        { value: "private",  label: "Private" },
      ],
      default: "public",
    },
    made_for_kids: {
      label: "Made for Kids",
      type: "bool",
      default: false,
    },
  },
  MSN: {
    syndication_allowed: {
      label: "Allow Syndication",
      type: "bool",
      default: true,
    },
  },
  FACEBOOK: {
    media_type: {
      label: "Media Type",
      type: "select",
      options: [
        { value: "VIDEO",   label: "Video" },
        { value: "REELS",   label: "Reels" },
        { value: "STORIES", label: "Stories" },
      ],
      default: "VIDEO",
    },
    published: {
      label: "Published (visible)",
      type: "bool",
      default: true,
    },
  },
};

/** Platforms available under a SOCIAL feed */
export const SOCIAL_FEED_PLATFORMS = [
  "YOUTUBE",
  "TIKTOK",
  "FACEBOOK",
  "INSTAGRAM",
  "PINTEREST",
  "SNAPCHAT",
  "THREADS",
];

/** Platforms available under a PARTNER (distribution) feed */
export const PARTNER_FEED_PLATFORMS = ["DAILYMOTION", "MSN", "OTHERS"];

/** Returns "SOCIAL" | "PARTNER" | null for a given platform. */
export const getPlatformFeedType = (platform) => {
  if (SOCIAL_FEED_PLATFORMS.includes(platform)) return "SOCIAL";
  if (PARTNER_FEED_PLATFORMS.includes(platform)) return "PARTNER";
  return null;
};

/**
 * True when both platforms belong to the same feed-type group.
 * A DUPLICATE source must satisfy this: brand-publish creates one queue item
 * per (feed × video) scoped to that feed's platforms, so duplicating across
 * feed types leaves a dangling reference the queue item can never resolve.
 */
export const isSamePlatformFeedType = (a, b) =>
  getPlatformFeedType(a) !== null && getPlatformFeedType(a) === getPlatformFeedType(b);

/** Platforms that have categories configured */
export const PLATFORMS_WITH_CATEGORIES = Object.keys(PLATFORM_CATEGORIES);

/**
 * True when a platform offers selectable categories but none has been chosen yet.
 * A brand default category (when configured) is pre-filled into config.category,
 * so this only returns true when the user genuinely still needs to pick one.
 */
export const isCategoryMissing = (platform, config) =>
  Boolean(PLATFORM_CATEGORIES[platform]) && !config?.category;

/** Platforms that show in the Platform Settings panel (have categories or extra config) */
export const PLATFORMS_WITH_SETTINGS = [
  ...new Set([
    ...Object.keys(PLATFORM_CATEGORIES),
    ...Object.keys(PLATFORM_EXTRA_CONFIG_SCHEMA),
  ]),
];
